import React from 'react'

/**
 * Debounce a function so it doesn't fire until waiting a set amount of time
 * @param {Function} func
 * @param {number} delay in milliseconds
 * @returns {Function}
 */
export function debounce(func, delay) {
  var timeout = null
  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(function() {
      func(...args)
    }, delay)
  }
}

/**
 * Delay the execution of function or state update.
 * @template T
 * @param {T} value - The value that you want to debounce. This can be of any type.
 * @param {number} delay - The delay time in milliseconds. After this amount of time, the latest value is used.
 * @returns {T} The debounced value. After the delay time has passed without the value changing, this will be updated to the latest value.
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
